import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { ApiError, type HouseCatalogo, type HouseClasse, type HouseModelo, type MinhaCasa } from '../api/types';
import { t } from '../i18n/idioma';
import { AcessoLista } from './casa/CasaUi';

/**
 * Corretor de casas (techspec-casas §02): o jogador escolhe a CASA (um modelo do UO) e o servidor aloca o lote, o
 * primeiro livre da classe do modelo. A prévia é a arte da casa inteira (`public/casas/modelos/<id>.png`).
 */
interface Props {
  npcId: string;
  active: boolean;
  onAnuncio(ev: { lugar: string }): void;
  onComprou(ev: { n: number; lugar: string; casa: string; minha: MinhaCasa }): void;
  /** Teleportar até a soleira do lote (abre a confirmação do Teleportar). */
  onIrParaCasa(minha: MinhaCasa): void;
  /** A casa foi devolvida: o lote voltou à venda. */
  onDevolveu(): void;
}

type Filtro = 'T' | HouseClasse;
const FILTROS: readonly Filtro[] = ['T', 'P', 'M', 'G', 'L'];
const VERSAO_PREVIA = 1;

export const nomeDoModelo = (id: string | null | undefined) => (id ? t(`casa.modelo.${id}`) : t('casa.semModelo'));
const previa = (id: string) => `/casas/modelos/${id}.png?v=${VERSAO_PREVIA}`;
const prata = (n: number) => n.toLocaleString();

function andaresDe(m: HouseModelo): string {
  const base = m.andares === 1 ? t('casa.umAndar') : t('casa.andares', { n: m.andares });
  return m.terraco ? `${base} ${t('casa.maisTerraco')}` : base;
}

export function HouseGump({ npcId, active, onAnuncio, onComprou, onIrParaCasa, onDevolveu }: Props) {
  const [cat, setCat] = useState<HouseCatalogo | null>(null);
  const [minha, setMinha] = useState<MinhaCasa | null>(null);
  const [filtro, setFiltro] = useState<Filtro>('T');
  const [sel, setSel] = useState<string | null>(null);
  const [confirmar, setConfirmar] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [anunciou, setAnunciou] = useState(false);
  const [devolver, setDevolver] = useState(false);
  /* Duas abas (techspec-casas §03.9): quem tem casa abre em "Minha casa"; "À venda" é o catálogo. Comprar leva para
     "Minha casa"; devolver volta para "À venda". */
  const [aba, setAba] = useState<'minha' | 'venda'>('venda');
  const temCasa = !!minha;
  useEffect(() => { setAba(temCasa ? 'minha' : 'venda'); }, [temCasa]);

  useEffect(() => {
    if (!active) return;
    let live = true;
    setError(null);
    void Promise.all([api.listarModelos(npcId), api.minhaCasa().catch(() => null)])
      .then(([lista, casa]) => {
        if (!live) return;
        setCat(lista);
        setMinha(casa ?? lista.minha);
        setSel(cur => cur ?? lista.modelos[0]?.id ?? null);
      })
      .catch(err => {
        if (!live) return;
        setError(err instanceof ApiError ? err.message : t('casa.busy'));
      });
    return () => { live = false; };
  }, [active, npcId]);

  useEffect(() => {
    if (!active || anunciou || !cat || minha) return;
    setAnunciou(true);
    onAnuncio({ lugar: cat.lugar });
  }, [active, anunciou, cat, minha, onAnuncio]);

  const visiveis = useMemo(
    () => (cat?.modelos ?? []).filter(m => filtro === 'T' || m.classe === filtro),
    [cat, filtro]
  );
  const modelo = cat?.modelos.find(m => m.id === sel) ?? visiveis[0] ?? null;

  const escolher = (id: string) => {
    setSel(id);
    setConfirmar(false);
    setError(null);
  };

  const comprar = async () => {
    if (!modelo || busy) return;
    if (!confirmar) {
      setConfirmar(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await api.comprarCasa(npcId, modelo.id);
      setMinha(r.minha);
      setConfirmar(false);
      setCat(c => c && {
        ...c,
        minha: r.minha,
        modelos: c.modelos.map(m => (m.classe === r.minha.classe ? { ...m, livres: Math.max(0, m.livres - 1) } : m))
      });
      onComprou({ n: r.minha.numero, lugar: r.minha.lugar, casa: nomeDoModelo(r.minha.modelo), minha: r.minha });
    } catch (err) {
      setError(msgErro(err));
      setConfirmar(false);
    } finally {
      setBusy(false);
    }
  };

  /** A lista de acesso (techspec-casas §03.13): papel, tirar, casa aberta. */
  const mudarAcesso = async (fn: () => Promise<MinhaCasa>) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      setMinha(await fn());
    } catch (err) {
      setError(msgErro(err));
    } finally {
      setBusy(false);
    }
  };

  const devolverCasa = async () => {
    if (busy) return;
    if (!devolver) {
      setDevolver(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.devolverCasa();
      setMinha(null);
      setDevolver(false);
      setCat(await api.listarModelos(npcId));
      onDevolveu();
    } catch (err) {
      setError(msgErro(err));
      setDevolver(false);
    } finally {
      setBusy(false);
    }
  };

  // Lote comprado antes dos modelos (sem casa): escolhe uma casa da classe do lote e ela é erguida nele.
  const loteVazio = !!minha && !minha.modelo;
  const bloqueio = !modelo ? ''
    : loteVazio ? (modelo.classe === minha!.classe ? '' : t('casa.outraClasse'))
    : minha ? t('casa.jaTem') : modelo.livres === 0 ? t('casa.semLote') : '';

  return (
    <>
      {error && <p className="notice" role="alert">{error}</p>}
      {!cat && !error && <p className="empty">{t('comum.montando')}</p>}
      {minha && (
        <div className="casa-abas" role="tablist" aria-label={t('casa.titulo')}>
          <button type="button" role="tab" aria-selected={aba === 'minha'} className="casa-aba" onClick={() => setAba('minha')}>
            {t('casa.minhaCasa')}
          </button>
          <button type="button" role="tab" aria-selected={aba === 'venda'} className="casa-aba" onClick={() => setAba('venda')}>
            {t('casa.aba.venda')}
          </button>
        </div>
      )}
      {minha && aba === 'minha' && (
        <div className="house-minha" role="tabpanel">
          {minha.modelo && (
            <div className="casa-ficha__arte casa-minha__arte"><img src={previa(minha.modelo)} alt={nomeDoModelo(minha.modelo)} /></div>
          )}
          <p className="casa-minha__linha">{t('casa.minhaLinha', { casa: nomeDoModelo(minha.modelo), n: minha.numero, lugar: minha.lugar })}</p>
          {!minha.modelo && <p className="casa-ficha__aviso">{t('casa.loteVazio', { classe: t(`casa.classe.${minha.classe}`) })}</p>}
          <button type="button" className="btn btn--primary casa-ir" disabled={busy} onClick={() => onIrParaCasa(minha)}>
            {t('casa.irParaCasa')}
          </button>
          <AcessoLista minha={minha} busy={busy}
            onPapel={(nome, papel) => void mudarAcesso(() => api.convidar(nome, papel))}
            onRemover={nome => void mudarAcesso(() => api.desconvidar(nome))}
            onAberta={aberta => void mudarAcesso(() => api.casaAberta(aberta))} />
          <div className="casa-devolver">
            <p className="casa-ficha__nota">{t('casa.devolverNota')}</p>
            <button type="button" className={`btn btn--tiny ${devolver ? 'btn--danger' : 'btn--ghost'}`} disabled={busy}
              onClick={() => void devolverCasa()} onBlur={() => setDevolver(false)}>
              {devolver ? t('casa.devolverConfirma') : t('casa.devolver')}
            </button>
          </div>
        </div>
      )}
      {cat && (!minha || aba === 'venda') && (
        <div className="casa-cat" role={minha ? 'tabpanel' : undefined}>
          <p className="casa-fala">{t('casa.fala')}</p>
          <div className="casa-filtros" role="tablist" aria-label={t('casa.titulo')}>
            {FILTROS.map(f => (
              <button
                key={f}
                type="button"
                role="tab"
                aria-selected={filtro === f}
                className="casa-filtro"
                onClick={() => setFiltro(f)}
              >
                {f === 'T' ? t('casa.filtro.todas') : t(`casa.classe.${f}`)}
              </button>
            ))}
          </div>
          <div className="casa-corpo">
            <ul className="casa-lista" aria-label={t('casa.titulo')}>
              {visiveis.map(m => (
                <li key={m.id}>
                  <button
                    type="button"
                    className="casa-item"
                    aria-pressed={modelo?.id === m.id}
                    onClick={() => escolher(m.id)}
                  >
                    <img src={previa(m.id)} alt="" loading="lazy" />
                    <span className="casa-item__txt">
                      <span className="casa-item__nome">{nomeDoModelo(m.id)}</span>
                      <span className="casa-item__sub">{t(`casa.classe.${m.classe}`)} · {andaresDe(m)}</span>
                    </span>
                    <span className="casa-item__preco">
                      {m.precoAgora <= 0 ? t('casa.gratis') : t('casa.preco', { n: prata(m.precoAgora) })}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            {modelo && (
              <div className="casa-ficha">
                <div className="casa-ficha__arte"><img src={previa(modelo.id)} alt={nomeDoModelo(modelo.id)} /></div>
                <h3 className="casa-ficha__nome">{nomeDoModelo(modelo.id)}</h3>
                <dl className="casa-ficha__dados">
                  <dt>{t('casa.f.andares')}</dt><dd>{andaresDe(modelo)}</dd>
                  <dt>{t('casa.f.planta')}</dt><dd>{t('casa.planta', { w: modelo.largura, h: modelo.altura })}</dd>
                  <dt>{t('casa.f.lote')}</dt><dd>{t(`casa.classe.${modelo.classe}`)} · {t('casa.lotesLivres', { n: modelo.livres })}</dd>
                  <dt>{t('casa.f.convidados')}</dt><dd>{modelo.convidados}</dd>
                  <dt>{t('casa.f.baus')}</dt><dd>{modelo.baus}</dd>
                  <dt>{t('casa.f.presos')}</dt><dd>{modelo.presos}</dd>
                  <dt>{t('casa.f.manequins')}</dt><dd>{modelo.manequins}</dd>
                  <dt>{t('casa.f.aluguel')}</dt><dd>{t('casa.aluguel', { n: prata(modelo.aluguel) })}</dd>
                  <dt>{t('casa.f.preco')}</dt>
                  <dd>
                    {modelo.precoAgora < modelo.preco && <s>{t('casa.preco', { n: prata(modelo.preco) })}</s>}{' '}
                    <b>{modelo.precoAgora <= 0 ? t('casa.gratisTeste') : t('casa.preco', { n: prata(modelo.precoAgora) })}</b>
                  </dd>
                </dl>
                <p className="casa-ficha__nota">{t('casa.loteDoCartorio')}</p>
                {bloqueio && <p className="casa-ficha__aviso">{bloqueio}</p>}
                <button
                  type="button"
                  className={`btn ${confirmar ? 'btn--primary' : ''}`}
                  disabled={busy || !!bloqueio}
                  onClick={() => void comprar()}
                >
                  {confirmar ? t('casa.confirmar') : loteVazio ? t('casa.erguer') : t('casa.comprar')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function msgErro(err: unknown): string {
  if (!(err instanceof ApiError)) return t('casa.busy');
  const conhecidos = ['casa.papel', 'casa.outraClasse', 'casa.naoVende', 'casa.vendida', 'casa.jaTem', 'casa.semPrata', 'casa.longe', 'casa.semLote',
    'casa.semCasa', 'casa.semPessoa', 'casa.convidadoSois', 'casa.cheia', 'casa.jaConvidado', 'casa.busy'];
  if (err.code && conhecidos.includes(err.code)) return err.message || t(err.code);
  return err.message || t('casa.busy');
}
